// src/design-system/components/TabPanelWrapper.tsx
import React, {ReactNode} from 'react'
import styled from 'styled-components'
import {Tabs} from './Tabs'

export interface TabPanelConfig {
  /** Tab title displayed in the header bar (left side). Required. */
  title: string
  /** Action buttons/icons rendered right-aligned in the header bar. */
  headerActions?: ReactNode
  /** Optional sub-tab bar rendered below the header using DS Tabs. */
  subTabs?: {
    items: {id: string; label: string}[]
    activeId: string
    onChange: (id: string) => void
    /** Right-aligned controls rendered on the SAME row as the sub-tabs (e.g. a
        search field), so they sit at the tab height instead of a separate row. */
    actions?: ReactNode
  }
  /** When true, body area has no padding. For full-bleed content like forms. */
  noPadding?: boolean
}

const PanelRoot = styled.div`
  background: var(--bg-card);
  min-height: 100%;
  /* Standardized section card — matches the hierarchy sidebar: 8px corner,
     subtle border, medium shadow. overflow:hidden clips the header / sub-tab
     dividers cleanly to the rounded corners. */
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-mid-strong);
  overflow: hidden;
`

/**
 * Tallest thing that can sit in a panel header, used to give headers a consistent height
 * whether or not they carry actions.
 *
 * NOT derived from PanelActions' `max-height` below: the DS Button sets its own
 * `min-height` (var(--touch-min) for md/lg, var(--btn-height-sm) for sm), and per CSS 2.1
 * §10.7 min-height is applied after max-height and wins — so that cap never takes effect
 * and a header with a md Button really is --touch-min tall. Header actions are also not
 * always buttons (SearchInput is --touch-min too), which the `button {}` selector misses.
 */
const HEADER_ACTION_HEIGHT = 'var(--touch-min)'

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-4) var(--space-6);
  border-bottom: var(--border-thin) solid var(--border-subtle);
  /* Header height parity: with actions the row is action-height + the vertical
     padding + the bottom divider (box-sizing is border-box app-wide), i.e.
     1.875rem + 2 x var(--space-4) + var(--border-thin). Setting that as the
     MIN height gives an action-less header the same height without capping a
     header that legitimately needs more room (wrapped title, taller control).
     align-items: center above keeps the title centred either way. */
  min-height: calc(${HEADER_ACTION_HEIGHT} + var(--space-4) * 2 + var(--border-thin));
`

const PanelTitle = styled.h3`
  font-weight: var(--fw-semibold);
  font-size: var(--fs-body);
  color: var(--neutral-800);
  margin: 0;
`

const PanelActions = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);

  /* No height cap here: the DS Button's own min-height wins over max-height (CSS 2.1
     §10.7), so a cap would be inert and misleading. PanelHeader's min-height is what
     keeps action-less headers the same height as these. */
  button {
  }
`

const SubTabsBar = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: var(--space-4);
  padding: 0 var(--space-6);
  border-bottom: var(--border-thin) solid var(--border-subtle);
`

const SubTabsActions = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  /* Vertical padding so a search field / controls don't sit flush against the
     sub-tab row's bottom divider line. */
  padding: var(--space-2) 0;
`

const PanelBody = styled.div<{$noPadding?: boolean}>`
  padding: ${({$noPadding}) => ($noPadding ? '0' : 'var(--space-4) var(--space-6)')};
`

interface TabPanelWrapperProps {
  config: TabPanelConfig
  children: ReactNode
  className?: string
  'data-testid'?: string
}

export const TabPanelWrapper: React.FC<TabPanelWrapperProps> = ({
  config,
  children,
  className,
  'data-testid': testId,
}) => {
  const {title, headerActions, subTabs, noPadding} = config

  return (
    <PanelRoot className={className} data-testid={testId}>
      <PanelHeader>
        <PanelTitle>{title}</PanelTitle>
        {headerActions && <PanelActions>{headerActions}</PanelActions>}
      </PanelHeader>
      {subTabs && (
        <SubTabsBar>
          <Tabs
            variant="underline"
            tabs={subTabs.items.map((item) => ({value: item.id, label: item.label}))}
            activeTab={subTabs.activeId}
            onChange={subTabs.onChange}
            // Gives every sub-tab a stable hook: `${testId}-subtabs-tab-${id}`. Without it the
            // sub-tab strip is only reachable by label text, which makes permission e2e brittle
            // and, for panels with no data-testid at all, impossible.
            data-testid={testId ? `${testId}-subtabs` : undefined}
          />
          {subTabs.actions && <SubTabsActions>{subTabs.actions}</SubTabsActions>}
        </SubTabsBar>
      )}
      <PanelBody $noPadding={noPadding}>{children}</PanelBody>
    </PanelRoot>
  )
}
